import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Grid } from "@mui/material";
import PopoverMenu from "../../PopoverMenu";
import { useDebounce } from "../../../../hooks/useDebounce";
import { ParagraphText, UBButton, UBInput } from "../../../../Libraries";
import { ReactComponent as DiscountIcon } from "../../../../assets/icons/discount.svg";
import AppAlert from "../../../../Libraries/AppAlert";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { BinIcon, NewMinusIcon, NewPlusIcon } from "../../../../assets/icons/icon";
import { LanguageContext } from "../../../../context/LanguageContext";
import styles from "./styles.module.css";
import { sentenceCase } from "../../../../constants";
import { analytics, triggerLogEvent } from "../../../../config/firebase";

const MAX_QUANTITY_DIGITS = 7;

function SaleItemRow({
  item,
  onUpdate = () => {},
  actionMenuOptions,
  onUpdateStockPrice = () => {},
  onDelete = () => {},
}) {
  const [I18n] = useContext(LanguageContext);
  const itemRef = useRef(item);
  const onUpdateRef = useRef(onUpdate);
  const itemDataRef = useRef(null);
  const suppressNextQtyBlurRef = useRef(false);

  useEffect(() => {
    itemRef.current = item;
  }, [item]);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const [itemData, setItemData] = useState({
    quantity: +(item?.quantity ?? 0),
    name: item?.name ?? "",
    price: item?.selling_price ?? 0,
  });

  itemDataRef.current = itemData;

  const [editState, setEditState] = useState({
    isEditingName: false,
    isEditingPrice: false,
  });

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (!item) return;
    setItemData({
      quantity: item.quantity,
      name: item.name,
      price: item.selling_price ?? 0,
    });
  }, [item, item?.quantity, item?.name, item?.selling_price]);

  const debouncedUpdateQuantity = useDebounce((newQuantity) => {
    onUpdateRef.current(itemRef.current, +newQuantity, "quantity");
  }, 300);

  const handleIncrement = useCallback(() => {
    triggerLogEvent(analytics, "pos_sale_item_qty_incremented", { item_name: itemRef.current?.name });
    setItemData((prev) => {
      const newQuantity = +prev.quantity + 1;
      if (String(newQuantity).length <= MAX_QUANTITY_DIGITS) {
        debouncedUpdateQuantity(newQuantity);
        return { ...prev, quantity: newQuantity };
      }
      return prev;
    });
  }, [debouncedUpdateQuantity]);

  const handleDecrement = useCallback(() => {
    const currentQty = +itemDataRef.current.quantity;
    if (currentQty > 1) {
      triggerLogEvent(analytics, "pos_sale_item_qty_decremented", { item_name: itemRef.current?.name });
      const newQuantity = currentQty - 1;
      setItemData((prev) => ({ ...prev, quantity: newQuantity }));
      debouncedUpdateQuantity(newQuantity);
    } else {
      triggerLogEvent(analytics, "pos_sale_item_removed", { item_name: itemRef.current?.name });
      onDelete(itemRef.current);
    }
  }, [debouncedUpdateQuantity, onDelete]);

  const handleQuantityChange = useCallback((_, value) => {
    setItemData((prev) => ({ ...prev, quantity: value }));
  }, []);

  const handleQuantitySave = useCallback(
    (raw) => {
      const quantity = +raw;
      if (!quantity) {
        triggerLogEvent(analytics, "pos_sale_item_removed", { item_name: itemRef.current?.name });
        onDelete(itemRef.current);
        return;
      }
      triggerLogEvent(analytics, "pos_sale_item_qty_changed", { item_name: itemRef.current?.name, quantity });
      setItemData((prev) => ({ ...prev, quantity }));
      onUpdateRef.current(itemRef.current, quantity, "quantity");
    },
    [onDelete],
  );

  const handleQuantityBlur = useCallback(
    (e) => {
      if (suppressNextQtyBlurRef.current) {
        suppressNextQtyBlurRef.current = false;
        return;
      }
      handleQuantitySave(e.target.value);
    },
    [handleQuantitySave],
  );

  const handleNameChange = useCallback((_, value) => {
    setItemData((prev) => ({ ...prev, name: value }));
  }, []);

  const handleNameSave = useCallback(() => {
    const { name } = itemDataRef.current;
    if (!name?.trim()) return;
    onUpdateRef.current(itemRef.current, name, "name");
    setEditState((s) => ({ ...s, isEditingName: false }));
  }, []);

  const handlePriceChange = useCallback((_, value) => {
    setItemData((prev) => ({ ...prev, price: +value }));
  }, []);

  const handlePriceSave = useCallback(
    (e) => {
      const rawPrice = +itemDataRef.current.price;
      if (
        item?.selling_price !== rawPrice &&
        item?._raw?.stock_id
      ) {
        setAnchorEl(e?.currentTarget ?? e?.target ?? null);
        setPopoverOpen(true);
        return;
      }
      onUpdateRef.current(itemRef.current, rawPrice, "selling_price");
      setEditState((s) => ({ ...s, isEditingPrice: false }));
    },
    [item?._raw?.stock_id, item?.selling_price],
  );

  const handleConfirmStockUpdate = useCallback(() => {
    const rawPrice = +itemDataRef.current.price;
    onUpdateRef.current(itemRef.current, rawPrice, "selling_price");
    onUpdateStockPrice(rawPrice);
    setPopoverOpen(false);
    setAnchorEl(null);
    setEditState((s) => ({ ...s, isEditingPrice: false }));
  }, [onUpdateStockPrice]);

  const handleDismissStockPrompt = useCallback(() => {
    setItemData((prev) => ({
      ...prev,
      price: item?.selling_price ?? 0,
    }));
    setPopoverOpen(false);
    setAnchorEl(null);
    setEditState((s) => ({ ...s, isEditingPrice: false }));
  }, [item?.selling_price]);

  const startEditName = useCallback(() => {
    if (!item?._raw?.stock_id) {
      setEditState((s) => ({ ...s, isEditingName: true }));
    }
  }, [item?._raw?.stock_id]);

  const startEditPrice = useCallback(() => {
    setEditState((s) => ({ ...s, isEditingPrice: true }));
  }, []);

  const blurSuppressedMouseDown = useCallback(() => {
    suppressNextQtyBlurRef.current = true;
  }, []);

  const canEditName = !item?._raw?.stock_id;
  const nameRowClass = canEditName
    ? `borderRadius6 hover_color_foundation_Blue transition_background_color pointer`
    : "";

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      sx={{ marginTop: "0px !important" }}
      flexWrap="nowrap"
      className={styles.rowContainer}
    >
      <Grid
        item
        flex={1}
        md={5}
        className={styles.itemNameWrap}
      >
        {editState.isEditingName ? (
          <UBInput
            inputId="item_name"
            value={itemData.name}
            onChange={handleNameChange}
            onBlur={handleNameSave}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleNameSave();
            }}
            baseClassname="input-row-style saleItemInput"
            autoFocus
            maxLength={100}
          />
        ) : (
          <ParagraphText
            variant="body1"
            className={`${nameRowClass} ${styles.itemRowStyle} truncate`}
            onClick={startEditName}
          >
            {sentenceCase(itemData.name)}
          </ParagraphText>
        )}
      </Grid>

      <Grid
        item
        flex={1}
        className={styles.quantityWrap}
      >
          <UBButton
            id="decrement_icon"
            type="button"
            title=""
            iconComponent={
              +itemData.quantity === 1 ? (
                <BinIcon />
              ) : (
                <NewMinusIcon width={20} height={20} />
              )
            }
            className={styles.qtyStepBtn}
            onClick={handleDecrement}
            onMouseDown={blurSuppressedMouseDown}
          />
          <UBInput
            inputId="quantity_id"
            value={itemData.quantity}
            onChange={handleQuantityChange}
            onBlur={handleQuantityBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleQuantitySave(itemData.quantity);
            }}
            className="inputQuantityStyle"
            inputClassname="quantityStyle"
            onlyNumbers
            maxLength={MAX_QUANTITY_DIGITS}
          />
          <UBButton
            id="increament_icon"
            type="button"
            title=""
            iconComponent={<NewPlusIcon width={20} height={20} />}
            className={styles.qtyStepBtn}
            onClick={handleIncrement}
            onMouseDown={blurSuppressedMouseDown}
          />
      </Grid>

      <Grid
        item
        flex={1}
        className={styles.priceWrap}
      >
        {editState.isEditingPrice ? (
          <>
            <UBInput
              inputId="price_id"
              value={itemData.price}
              onChange={handlePriceChange}
              onBlur={handlePriceSave}
              onKeyDown={(e) => {
                if (e.key === "Enter") handlePriceSave(e);
              }}
              baseClassname="input-row-style"
              autoFocus
              onlyNumbers
              maxLength={10}
            />
            <AppAlert
              open={popoverOpen}
              anchorEl={anchorEl}
              onClose={handleDismissStockPrompt}
              onConfirm={handleConfirmStockUpdate}
              onCancel={handleDismissStockPrompt}
              title={I18n.update_item_price}
              message={I18n.stock_list_desc}
            />
          </>
        ) : (
          <ParagraphText
            variant="body1"
            className={`${styles.itemRowStyle} ${styles.priceStyle}`}
            onClick={startEditPrice}
          >
            Rs. {formatNum(itemData.price)}
            {item?.discount ? (
              <ParagraphText
                className={`fontSize14 foundationInk300 ${styles.discountStyle}`}
              >
                <span className={styles.discountIconStyle}>
                  <DiscountIcon />
                </span>
                {item?.discount_method === "rs" ? "Rs. " : ""}
                {item?.discount}
                {item?.discount_method === "%" ? "%" : ""}
              </ParagraphText>
            ) : null}
          </ParagraphText>
        )}
      </Grid>

      <Grid
        item
        className={styles.actionWrap}
      >
        <PopoverMenu
          id="sale_item_row"
          menuOptions={actionMenuOptions}
          popOverStyle={{
            marginLeft: "5px",
            borderRadius: "16px !important",
          }}
          buttonStyle={{ width: "32px", height: "32px" }}
        />
      </Grid>
    </Grid>
  );
}

export default memo(SaleItemRow);
